/* eslint-disable max-lines -- a lot of contract stuff */
import { G } from '@mobily/ts-belt';
import { Decimal } from 'decimal.js';
import { z } from 'zod';

import { erContractTypes, esContractTypes } from '@Common/types/b2b/contracts';
import { ZodB2bAdminStatus } from '@Common/types/bo/b2b/admins';
import { ZodB2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts/campaign_action_handler';
import {
  type B2bContractType,
  ZodB2bContractType,
} from '@Common/types/bo/b2b/contracts/contract_type';
import { getContractTypeDefinition } from '@Common/types/bo/b2b/contracts/contract_type_definitions';
import { ZodB2bContractFees } from '@Common/types/bo/b2b/contracts/fees';
import { ZodBoExportResponse, ZodBoListCsvExportBody } from '@Common/types/bo/exports';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodDate } from '@Common/types/date';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { ZodDateString } from '@Common/types/zod/zod-date-string';
import { ZodSecureEmail } from '@Common/types/zod/zod-email';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';
import {
  getContractDefinitionErrors,
  INVALID_ALLOCATION_CHOICE_COMBINATION_ERROR,
  isAllCampaignsProcessingOptionsSimilar,
  isERContract,
  isValidAllocationChoiceCombination,
} from '@Common/validators/contracts';

export const B2bContractRelationshipTypeValues = ['direct', 'indirect'] as const;
export const ZodB2bContractRelationshipType = z.enum(B2bContractRelationshipTypeValues);

// Declared in dedicated leaf modules so that modules reachable from `contracts/fees`
// can consume them without creating an import cycle back through this file.
export {
  type B2bCampaignActionHandler,
  B2bCampaignActionHandlerValues,
  ZodB2bCampaignActionHandler,
} from '@Common/types/bo/b2b/contracts/campaign_action_handler';
export {
  type B2bContractType,
  ZodB2bContractType,
} from '@Common/types/bo/b2b/contracts/contract_type';

export const B2bMatchingContributionPaymentFrequencyValues = [
  '1_month',
  '3_months',
  '6_months',
  '12_months',
] as const;
export const ZodB2bMatchingContributionPaymentFrequency = z.enum(
  B2bMatchingContributionPaymentFrequencyValues,
);
export type B2bMatchingContributionPaymentFrequency = z.TypeOf<
  typeof ZodB2bMatchingContributionPaymentFrequency
>;

export const B2bTeamValues = ['standard', 'middle', 'key_account', 'international'] as const;

export const ZodB2bTeam = z.enum(B2bTeamValues);

export type B2bTeam = z.TypeOf<typeof ZodB2bTeam>;

// Frequency for the paper version of the transaction statements
const B2bPaperTransactionStatementFrequencyValues = [
  'digital_only',
  '1_month',
  '3_months',
  '6_months',
  '12_months',
  'every_time',
] as const;

export const ZodB2bPaperTransactionStatementFrequencyValues = z.enum(
  B2bPaperTransactionStatementFrequencyValues,
);

export type B2bPaperTransactionStatementFrequency = z.TypeOf<
  typeof ZodB2bPaperTransactionStatementFrequencyValues
>;

const B2bContractStatusValues = [
  'offer',
  'effective',
  'suspended',
  'denounced',
  'process_of_creation',
  'closed_by_amendment',
  'other',
  'terminated',
] as const;

export const ZodB2bContractStatus = z.enum(B2bContractStatusValues);

export type B2bContractStatus = z.TypeOf<typeof ZodB2bContractStatus>;

export function hasERContracts(contractTypes: B2bContractType[]): boolean {
  return contractTypes.some((type) => erContractTypes.includes(type));
}

export function hasESorSOLESContracts(contractTypes: B2bContractType[]): boolean {
  return contractTypes.some((type) => esContractTypes.includes(type));
}

const ZodContributionAgreementDetails = z.object({
  agreement_starting_date: ZodDate,
  agreement_end_date: ZodDate.nullable(),
  is_agreement_common_for_group: z.boolean(),
});

export type B2bContributionAgreementDetails = z.TypeOf<typeof ZodContributionAgreementDetails>;

export const B2bDaysIncrementStepValues = [0.25, 0.5] as const;
export const ZodB2bDaysIncrementStep = z.literal(B2bDaysIncrementStepValues);
export type B2bDaysIncrementStep = z.TypeOf<typeof ZodB2bDaysIncrementStep>;

const ZodUnusedVacationDaysAgreementDetails = z.object({
  ...ZodContributionAgreementDetails.shape,
  increment_step: ZodB2bDaysIncrementStep.nullable(),
});

const ZodMonetizedDaysOffDetails = z.object({
  increment_step: ZodB2bDaysIncrementStep.nullable(),
});

export type B2bMonetizedDaysOffDetails = z.TypeOf<typeof ZodMonetizedDaysOffDetails>;

export const ZodB2bContractParticipationParameters = z
  .object({
    ...ZodContributionAgreementDetails.shape,
    distribution_uniformity_pct: z.number().multipleOf(0.01).min(0).nullable(),
    distribution_salary_pct: z.number().multipleOf(0.01).min(0).nullable(),
    distribution_tenure_pct: z.number().multipleOf(0.01).min(0).nullable(),
  })
  .nullable()
  .refine(
    (val) =>
      !val ||
      G.isNullable(val.distribution_salary_pct) ||
      G.isNullable(val.distribution_tenure_pct) ||
      G.isNullable(val.distribution_uniformity_pct) ||
      new Decimal(val.distribution_salary_pct)
        .add(val.distribution_tenure_pct)
        .add(val.distribution_uniformity_pct)
        .equals(100),
    'Total of participation distribution should be 100%.',
  );

export type B2bContractParticipationParameters = z.TypeOf<
  typeof ZodB2bContractParticipationParameters
>;

export const ZodB2bContractProfitSharingParameters = z
  .object({
    ...ZodContributionAgreementDetails.shape,
    is_tacitly_renewed: z.boolean().nullable(),
    distribution_uniformity_pct: z.number().multipleOf(0.01).min(0).nullable(),
    distribution_salary_pct: z.number().multipleOf(0.01).min(0).nullable(),
    distribution_tenure_pct: z.number().multipleOf(0.01).min(0).nullable(),
  })
  .nullable()
  .refine(
    (val) =>
      !val ||
      G.isNullable(val.distribution_salary_pct) ||
      G.isNullable(val.distribution_tenure_pct) ||
      G.isNullable(val.distribution_uniformity_pct) ||
      new Decimal(val.distribution_salary_pct)
        .add(val.distribution_tenure_pct)
        .add(val.distribution_uniformity_pct)
        .equals(100),
    'Total of profit sharing distribution should be 100%.',
  );

export type B2bContractProfitSharingParameters = z.TypeOf<
  typeof ZodB2bContractProfitSharingParameters
>;

const ZodCampaignProcessingOptions = z.object({
  allocation_computed_by: ZodB2bCampaignActionHandler.nullable(),
  choice_collected_by: ZodB2bCampaignActionHandler.nullable(),
  payout_by: ZodB2bCampaignActionHandler.nullable(),
  matching_contribution_computed_by: ZodB2bCampaignActionHandler.nullable(),
});

export type B2bCampaignProcessingOptions = z.TypeOf<typeof ZodCampaignProcessingOptions>;

const ZodCampaignsProcessingOptionsByCashSources = z.object({
  participation: ZodCampaignProcessingOptions.nullable(),
  profit_sharing: ZodCampaignProcessingOptions.nullable(),
  value_sharing_bonus: ZodCampaignProcessingOptions.nullable(),
  voluntary_deposit: ZodCampaignProcessingOptions.nullable(),
  unused_vacation_days: ZodCampaignProcessingOptions.nullable(),
});

export type B2bCampaignsProcessingOptionsByCashSources = z.TypeOf<
  typeof ZodCampaignsProcessingOptionsByCashSources
>;

export const ZodDayMonth = ZodTrimmedString.regex(/^\d{2}\/\d{2}$/, {
  message: 'Expected format DD/MM (e.g. 01/12)',
}).refine(
  (s) => {
    const [dayStr, monthStr] = s.split('/');
    const day = Number(dayStr);
    const month = Number(monthStr);

    if (!Number.isInteger(day) || !Number.isInteger(month)) {
      return false;
    }
    if (month < 1 || month > 12 || day < 1) {
      return false;
    }

    const maxDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= maxDays[month - 1];
  },
  { message: 'Invalid day for the given month' },
);

export const ZodB2bContract = z.object({
  id: ZodId,
  b2b_company_id: ZodId,
  contract_type: ZodB2bContractType,
  status: ZodB2bContractStatus.optional(),
  team: ZodB2bTeam.nullable(),
  contract_number: ZodTrimmedString
    // For migrated companies, contract_number = company external_code_enterprise
    .nullable(),
  label: ZodTrimmedString.nullable(),
  insurer_company: ZodTrimmedString.nullable(),
  support_phone_number: ZodTrimmedString.nullable(),
  signature_date: ZodDate.nullable(),
  effective_date: ZodDate.nullable(),
  end_date: ZodDate.nullable(),
  matching_contribution_cut_off_date: ZodDayMonth.nullable(),
  matching_contributions_enabled: z.boolean(),
  matching_contribution_payment_frequency: ZodB2bMatchingContributionPaymentFrequency.nullable(),
  is_matching_contribution_displayed: z.boolean().default(true),
  total_contribution_ceiling: z.number().int().nullable(),
  is_active: z.boolean(),
  relationship_type: ZodB2bContractRelationshipType,
  filib_url: ZodTrimmedString.url().nullable(),
  offer_code: ZodTrimmedString.optional().nullable(),
  paper_transaction_statement_frequency: ZodB2bPaperTransactionStatementFrequencyValues.optional(),
  version: z.number().int(),
  version_label: ZodTrimmedString.nullable(),
  is_liv: z.boolean().default(true),
  is_group_contract: z.boolean(),
  participation: ZodB2bContractParticipationParameters,
  profit_sharing: ZodB2bContractProfitSharingParameters,
  value_sharing: ZodContributionAgreementDetails.nullable(),
  unused_vacation_days: ZodUnusedVacationDaysAgreementDetails.nullable(),
  monetized_days_off: ZodMonetizedDaysOffDetails.nullable(),
  pee_agreement: ZodContributionAgreementDetails.nullable(),
  pereco_agreement: ZodContributionAgreementDetails.nullable(),
  pero_agreement: ZodContributionAgreementDetails.nullable(),
  transfer: z
    .object({
      is_competition_transfer: z.boolean(),
      external_custodian: ZodTrimmedString.optional().nullable(),
      is_soles_transfer: z.boolean(),
    })
    .nullable(),
  campaigns: ZodCampaignsProcessingOptionsByCashSources,
  created_at: z.date(),
  updated_at: z.date(),
  dist_case_number: ZodTrimmedString.nullable(),
  dist_case_reception_date: ZodDateString.nullable(),
  dist_correspondent_id: ZodId.nullable(),
  dist_correspondent_firstname: ZodTrimmedString.nullable(),
  dist_correspondent_lastname: ZodTrimmedString.nullable(),
  dist_correspondent_portfolio_code: ZodTrimmedString.nullable(),
  dist_correspondent_orias_code: ZodTrimmedString.nullable(),
  dist_correspondent_fia_code: ZodTrimmedString.nullable(),
  dist_correspondent_email: ZodSecureEmail.nullable(),
  dist_correspondent_email_state: z.enum(['blocked', 'permanently_blocked', 'invalid']).nullable(),
  dist_correspondent_landline: ZodPhoneNumber.nullable(),
  dist_correspondent_phone_number: ZodPhoneNumber.nullable(),
  dist_correspondent_collective_inspector_email: ZodSecureEmail.nullable(),
  dist_correspondent_street: ZodTrimmedString.nullable(),
  dist_correspondent_postal_code: ZodTrimmedString.nullable(),
  dist_correspondent_city: ZodTrimmedString.nullable(),
  dist_correspondent_country: ZodCountryCode.nullable(),
  dist_correspondent_company_name: ZodTrimmedString.nullable(),
  dist_correspondent_assistance_email: ZodTrimmedString.nullable(),
  dist_status: ZodB2bAdminStatus.nullable(),
});

export type B2bContract = z.TypeOf<typeof ZodB2bContract>;

const ZodB2bContractAgreements = ZodB2bContract.pick({
  participation: true,
  profit_sharing: true,
  value_sharing: true,
  unused_vacation_days: true,
  monetized_days_off: true,
  pee_agreement: true,
  pereco_agreement: true,
  pero_agreement: true,
  transfer: true,
});

export type B2BContractAgreements = z.infer<typeof ZodB2bContractAgreements>;

const ZodB2bContractDistCorrespondent = ZodB2bContract.pick({
  dist_correspondent_firstname: true,
  dist_correspondent_lastname: true,
  dist_correspondent_portfolio_code: true,
  dist_correspondent_orias_code: true,
  dist_correspondent_fia_code: true,
  dist_correspondent_email: true,
  dist_correspondent_landline: true,
  dist_correspondent_phone_number: true,
  dist_correspondent_collective_inspector_email: true,
  dist_correspondent_street: true,
  dist_correspondent_postal_code: true,
  dist_correspondent_city: true,
  dist_correspondent_country: true,
  dist_correspondent_company_name: true,
  dist_correspondent_assistance_email: true,
});
export type B2bContractDistCorrespondent = z.infer<typeof ZodB2bContractDistCorrespondent>;

export const ZodGetB2bContractsResponse = z.array(
  ZodB2bContract.omit({ dist_correspondent_email_state: true }),
);
export type GetB2bContractsResponse = z.TypeOf<typeof ZodGetB2bContractsResponse>;

const ZodPostB2bCompanyContractBodyBase = z
  .object({
    ...ZodB2bContract.omit({
      id: true,
      end_date: true,
      created_at: true,
      updated_at: true,
      version: true,
      version_label: true,
      campaigns: true,
      contract_type: true,
      insurer_company: true,

      dist_correspondent_id: true,
      dist_correspondent_firstname: true,
      dist_correspondent_lastname: true,
      dist_correspondent_portfolio_code: true,
      dist_correspondent_orias_code: true,
      dist_correspondent_fia_code: true,
      dist_correspondent_email: true,
      dist_correspondent_email_state: true,
      dist_correspondent_landline: true,
      dist_correspondent_phone_number: true,
      dist_correspondent_collective_inspector_email: true,
      dist_correspondent_street: true,
      dist_correspondent_postal_code: true,
      dist_correspondent_city: true,
      dist_correspondent_country: true,
      dist_correspondent_company_name: true,
      dist_correspondent_assistance_email: true,
      dist_status: true,
    }).shape,
    campaigns: ZodCampaignsProcessingOptionsByCashSources,
    contract_type: ZodB2bContractType.exclude(['er_external']),
    fees: ZodB2bContractFees.nullable().optional(),
    is_migrated: z.boolean().optional(),
    id_external_taiga: z.number().int().optional(),
    dist_correspondent_id: ZodId.nullable().optional(),
    dist_case_number: ZodTrimmedString.nullable().optional(),
    dist_case_reception_date: ZodDateString.nullable().optional(),
    // API created contracts have 'effective' status
    effective_date: ZodDate.optional().default(() => new Date()),
  })
  .refine(
    (val) =>
      val.participation === null ||
      val.campaigns.participation?.allocation_computed_by !== 'distributor' ||
      (G.isNotNullable(val.participation.distribution_uniformity_pct) &&
        G.isNotNullable(val.participation.distribution_salary_pct) &&
        G.isNotNullable(val.participation.distribution_tenure_pct)),
    'When distribution is set as allocator, then participation distribution should be set',
  )
  .refine(
    (val) =>
      val.profit_sharing === null ||
      val.campaigns.profit_sharing?.allocation_computed_by !== 'distributor' ||
      (G.isNotNullable(val.profit_sharing.distribution_uniformity_pct) &&
        G.isNotNullable(val.profit_sharing.distribution_salary_pct) &&
        G.isNotNullable(val.profit_sharing.distribution_tenure_pct)),
    'When distribution is set as allocator, then profit_sharing distribution should be set',
  )
  .refine(
    (val) => {
      if (
        val.matching_contributions_enabled &&
        (val.matching_contribution_payment_frequency === null ||
          val.matching_contribution_cut_off_date === null)
      ) {
        return false;
      }
      if (
        !val.matching_contributions_enabled &&
        (val.matching_contribution_payment_frequency !== null ||
          val.matching_contribution_cut_off_date !== null)
      ) {
        return false;
      }
      return true;
    },
    { message: 'Matching configuration config is incorrect' },
  )
  .refine(
    (val) => {
      return isERContract(val.contract_type) !== !!val.fees;
    },
    { message: 'Fees must only be set for non ER contracts' },
  )
  .refine(
    (val) =>
      getContractTypeDefinition(val.contract_type).campaigns.allowsProcessingOptionsByCashSource ||
      isAllCampaignsProcessingOptionsSimilar(val.campaigns),
    {
      message:
        'On standard contract, campaigns processing options should be the same on all cash sources',
    },
  )
  .refine(
    (val) =>
      Object.values(val.campaigns).every((cashSource) =>
        isValidAllocationChoiceCombination(
          cashSource?.allocation_computed_by,
          cashSource?.choice_collected_by,
        ),
      ),
    { message: INVALID_ALLOCATION_CHOICE_COMBINATION_ERROR },
  )
  .superRefine((val, ctx) => {
    getContractDefinitionErrors(val.contract_type, val).forEach((message) => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    });
  });

const ZodPostB2bCompanyContractBodyErExternal = z.object({
  contract_type: z.enum(['er_external']),
  b2b_company_id: ZodId,
  contract_number: ZodTrimmedString,
  insurer_company: ZodTrimmedString,
  label: ZodTrimmedString,
});

export const ZodPostB2bCompanyContractBody = z.union([
  ZodPostB2bCompanyContractBodyErExternal,
  ZodPostB2bCompanyContractBodyBase,
]);

export type PostB2bCompanyContractBody = z.TypeOf<typeof ZodPostB2bCompanyContractBody>;

const ZodB2bContractsFilters = z
  .object({
    ...ZodB2bContract.pick({
      contract_number: true,
      relationship_type: true,
      version: true,
      is_active: true,
    }).shape,
    id: createZodArrayable(ZodId),
    b2b_company_id: createZodArrayable(ZodId),
    contract_type: createZodArrayable(ZodB2bContractType),
    is_liv: createZodArrayable(z.boolean()),
    not_status: createZodArrayable(ZodB2bContractStatus),
  })
  .partial();

export type B2bContractsFilters = z.TypeOf<typeof ZodB2bContractsFilters>;

export const getB2bContractsSortColumns: (keyof GetB2bContractsResponse[number])[] = [
  'id',
  'created_at',
  'version',
  'is_active',
  'effective_date',
];

export const ZodB2bContractsQuery = buildZodBoListQueryParsed(
  ZodB2bContractsFilters,
  getB2bContractsSortColumns,
  {
    range: [0, 100],
  },
);
export type B2bContractsQuery = z.TypeOf<typeof ZodB2bContractsQuery>;

const ZodGetB2bContractsOneResponse = z.object({
  ...ZodB2bContract.shape,
  fees: ZodB2bContractFees.nullable(),
});
export type GetB2bContractsOneResponse = z.TypeOf<typeof ZodGetB2bContractsOneResponse>;

const ZodIdResponse = z.object({
  id: ZodId,
});

export const postB2bContractEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bCompanyContractBody,
  responseBody: ZodIdResponse,
});
export type PostB2bContractEndpoint = GetEndpointType<typeof postB2bContractEndpoint>;

const ZodPutB2bCompanyContractBody = z.object({
  fees: ZodB2bContractFees.nullable().optional(),
  label: ZodTrimmedString.max(50).nullable().optional(),
  applies_at: z.enum(['now', 'next-year']),
  signed_amendment_document: z.object({
    document_name: ZodTrimmedString,
    presigned_path: ZodTrimmedString,
  }),
  is_liv: z.boolean().optional(),
  filib_url: ZodTrimmedString.url().nullish(),
});

export const putB2bContractEndpoint = endpoint({
  method: 'PUT',
  body: ZodPutB2bCompanyContractBody,
  responseBody: ZodIdResponse,
  pathParams: ZodIDPathParam,
});
export type PutB2bContractEndpoint = GetEndpointType<typeof putB2bContractEndpoint>;

const ZodPutB2bCompanyContractEditBody = ZodB2bContract.pick({
  filib_url: true,
  contract_number: true,
  team: true,
  participation: true,
  value_sharing: true,
  profit_sharing: true,
  pee_agreement: true,
  pereco_agreement: true,
  pero_agreement: true,
  campaigns: true,
  contract_type: true,
  is_group_contract: true,
  matching_contribution_cut_off_date: true,
  matching_contribution_payment_frequency: true,
  matching_contributions_enabled: true,
  is_matching_contribution_displayed: true,
  transfer: true,
  unused_vacation_days: true,
  monetized_days_off: true,
  offer_code: true,
  total_contribution_ceiling: true,
  is_liv: true,
  status: true,
  dist_case_number: true,
  dist_case_reception_date: true,
  dist_correspondent_id: true,
})
  .refine(
    (val) =>
      val.participation === null ||
      val.campaigns.participation?.allocation_computed_by !== 'distributor' ||
      (G.isNotNullable(val.participation.distribution_uniformity_pct) &&
        G.isNotNullable(val.participation.distribution_salary_pct) &&
        G.isNotNullable(val.participation.distribution_tenure_pct)),
    'When distribution is set as allocator, then participation distribution should be set',
  )
  .refine(
    (val) =>
      val.profit_sharing === null ||
      val.campaigns.profit_sharing?.allocation_computed_by !== 'distributor' ||
      (G.isNotNullable(val.profit_sharing.distribution_uniformity_pct) &&
        G.isNotNullable(val.profit_sharing.distribution_salary_pct) &&
        G.isNotNullable(val.profit_sharing.distribution_tenure_pct)),
    'When distribution is set as allocator, then profit_sharing distribution should be set',
  )
  .refine(
    (val) => {
      if (
        val.matching_contributions_enabled &&
        (val.matching_contribution_payment_frequency === null ||
          val.matching_contribution_cut_off_date === null)
      ) {
        return false;
      }
      if (
        !val.matching_contributions_enabled &&
        (val.matching_contribution_payment_frequency !== null ||
          val.matching_contribution_cut_off_date !== null)
      ) {
        return false;
      }
      return true;
    },
    { message: 'Matching configuration config is incorrect' },
  )
  .refine(
    (val) =>
      getContractTypeDefinition(val.contract_type).campaigns.allowsProcessingOptionsByCashSource ||
      isAllCampaignsProcessingOptionsSimilar(val.campaigns),
    {
      message:
        'On standard contract, campaigns processing options should be the same on all cash sources',
    },
  )
  .refine(
    (val) =>
      Object.values(val.campaigns).every((cashSource) =>
        isValidAllocationChoiceCombination(
          cashSource?.allocation_computed_by,
          cashSource?.choice_collected_by,
        ),
      ),
    { message: INVALID_ALLOCATION_CHOICE_COMBINATION_ERROR },
  );

export type PutB2bContractEditRequest = z.infer<typeof ZodPutB2bCompanyContractEditBody>;
export type PutB2bContractEditResponse = z.infer<typeof ZodIDPathParam>;
export const putB2bContractEditEndpoint = endpoint({
  method: 'PUT',
  body: ZodPutB2bCompanyContractEditBody,
  responseBody: ZodIdResponse,
  pathParams: ZodIDPathParam,
});
export type PutB2bContractEditEndpoint = GetEndpointType<typeof putB2bContractEditEndpoint>;

export const getB2bContractsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodB2bContractsQuery,
  responseBody: ZodGetB2bContractsResponse,
});
export type GetB2bContractsEndpoint = GetEndpointType<typeof getB2bContractsEndpoint>;

/** The contracts of the list, as its filters and sort select them, in an Excel workbook */
export const ZodB2bContractsExportBody = z.object({
  kind: z.literal('b2b_contracts'),
  query: ZodBoListCsvExportBody.shape.query,
});
export type B2bContractsExportBody = z.infer<typeof ZodB2bContractsExportBody>;

export const postB2bContractsExportEndpoint = endpoint({
  method: 'POST',
  body: ZodB2bContractsExportBody,
  responseBody: ZodBoExportResponse,
});
export type PostB2bContractsExportEndpoint = GetEndpointType<typeof postB2bContractsExportEndpoint>;

export const getB2bContractsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bContractsOneResponse,
});
export type GetB2bContractsIdEndpoint = GetEndpointType<typeof getB2bContractsIdEndpoint>;
